import React, { useState, useMemo, useEffect } from "react";
import {
    Table, Button, Input, Space, Tag, Typography, Drawer, Descriptions, Empty, Spin, List, Divider, Select,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, PrinterOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSale } from "../../store/saleContext";
import { useProduct } from "../../store/productContext";
import { useCustomer } from "../../store/customerContext";
import { SellInvoice } from "../../types/sale";
import InvoicePrintHandler from "../../components/Print/InvoicePrintHandler";
import PageNavigation from "../Layout/PageNavigation";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
const { Title, Text } = Typography;

const getValidCurrency = (currency: string | undefined) => {
    if (!currency) return "USD";
    if (currency === "IQ") return "IQD";
    return currency;
};

const statusColors: any = {
    ["Paid"]: "green", ["Unpaid"]: "red", ["Partial"]: "orange",
    ["پارەدراو"]: "green", ["نەدراوە"]: "red", ["بەشێک"]: "orange",
    ["مدفوع"]: "green", ["غير مدفوع"]: "red", ["جزئي"]: "orange"
};

const getPaidStatus = (paid: number, remain: number, t: (key: string) => string) => {
    if (paid === 0) return t("unpaid");
    if (remain === 0) return t("paid");
    return t("partial");
};

// Canonical (non-translated) status key, used for filtering.
const getPaidStatusKey = (paid: number, remain: number): "paid" | "unpaid" | "partial" => {
    if (paid === 0) return "unpaid";
    if (remain === 0) return "paid";
    return "partial";
};

const getCustomerName = (customer: any, customerName: string | undefined, customers: any[]) => {
    if (customer) {
        return customer?.name || "-";
    }
    return customerName || "-";
};

const getProductName = (product: any, products: any[]) => {
    if (!product) return "-";
    if (typeof product === "object") return product.name || "-";
    // product is a plain string ID — look it up
    const found = products.find((p: any) => p.id === product || p._id === product);
    return found ? found.name : "-";
};

interface SaleListProps {
    onEdit: (sale: SellInvoice) => void;
    onAdd?: () => void;
}

const SaleList: React.FC<SaleListProps> = ({ onEdit, onAdd }) => {
    const { t, i18n } = useTranslation();
    const { sales, fetchSales, deleteSale } = useSale();
    const { products } = useProduct();
    const { customers, fetchCustomers } = useCustomer();
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "unpaid" | "partial">("all");
    const [selectedSale, setSelectedSale] = useState<SellInvoice | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Print state
    const [printInvoice, setPrintInvoice] = useState<SellInvoice | null>(null);

    useEffect(() => {
        if (sales.length === 0) fetchSales();
        if (customers.length === 0) fetchCustomers();
    }, []);

    const filteredSales = useMemo(() => {
        const q = search.toLowerCase();
        return sales.filter((s: any) => {
            if (statusFilter !== "all" && getPaidStatusKey(s.paid, s.remain) !== statusFilter) {
                return false;
            }
            const status = getPaidStatus(s.paid, s.remain, t).toLowerCase();
            const phone = (
                (s.customer && typeof s.customer === "object" ? s.customer.phone : "") ||
                s.customerPhone ||
                ""
            ).toLowerCase();
            return (
                (s.invoiceNumber ?? "").toLowerCase().includes(q) ||
                getCustomerName(s.customer, s.customerName, customers).toLowerCase().includes(q) ||
                phone.includes(q) ||
                (s.currency ?? "").toLowerCase().includes(q) ||
                String(s.total ?? "").includes(q) ||
                (s.note ?? "").toLowerCase().includes(q) ||
                status.includes(q) ||
                (s.date ? new Date(s.date).toLocaleDateString() : "").includes(q)
            );
        });
    }, [sales, search, statusFilter, customers, t]);

    const openDrawer = (sale: SellInvoice) => {
        setSelectedSale(sale);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedSale(null);
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const dark: [number, number, number]   = [30, 41, 59];
        const accent: [number, number, number] = [59, 130, 246];
        const white: [number, number, number]  = [255, 255, 255];
        const light: [number, number, number]  = [241, 245, 249];
        const today = new Date().toLocaleDateString("en-GB");

        // ── Header bar ──────────────────────────────────────────────────────
        doc.setFillColor(...dark);
        doc.rect(0, 0, pageWidth, 26, "F");

        doc.setTextColor(...white);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(17);
        doc.text("Sales Invoices Report", 14, 12);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Generated: ${today}`, 14, 20);
        doc.text(`Total records: ${filteredSales.length}`, pageWidth / 2, 20, { align: "center" });

        // ── Currency totals (top-right) ──────────────────────────────────────
        const totalsMap: Record<string, { total: number; paid: number; remain: number; discount: number }> = {};
        filteredSales.forEach((s: any) => {
            const cur = s.currency || "USD";
            if (!totalsMap[cur]) totalsMap[cur] = { total: 0, paid: 0, remain: 0, discount: 0 };
            totalsMap[cur].total    += s.total    || 0;
            totalsMap[cur].paid     += s.paid     || 0;
            totalsMap[cur].remain   += s.remain   || 0;
            totalsMap[cur].discount += s.discount || 0;
        });

        let ry = 11;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        Object.entries(totalsMap).forEach(([cur, vals]) => {
            doc.text(
                `${cur}: ${vals.total.toLocaleString()}  Paid: ${vals.paid.toLocaleString()}  Remain: ${vals.remain.toLocaleString()}`,
                pageWidth - 14, ry, { align: "right" }
            );
            ry += 7;
        });

        // ── Status badges helper ─────────────────────────────────────────────
        const statusLabel = (s: any) => {
            if (s.paid === 0)      return "Unpaid";
            if (s.remain === 0)    return "Paid";
            return "Partial";
        };
        const statusFill = (label: string): [number, number, number] => {
            if (label === "Paid")    return [22, 163, 74];
            if (label === "Unpaid")  return [220, 38, 38];
            return [234, 88, 12];
        };

        // ── Table ────────────────────────────────────────────────────────────
        const rows = filteredSales.map((s: any, idx: number) => {
            const cust = getCustomerName(s.customer, s.customerName, customers);
            const date = s.date ? new Date(s.date).toLocaleDateString("en-GB") : "-";
            const status = statusLabel(s);
            return [
                idx + 1,
                s.invoiceNumber || "-",
                cust,
                s.currency || "-",
                (s.discount || 0).toLocaleString(),
                (s.total  || 0).toLocaleString(),
                (s.paid   || 0).toLocaleString(),
                (s.remain || 0).toLocaleString(),
                status,
                date,
            ];
        });

        autoTable(doc, {
            startY: 31,
            margin: { left: 10, right: 10 },
            head: [["#", "Invoice #", "Customer", "Currency", "Discount", "Total", "Paid", "Remain", "Status", "Date"]],
            body: rows,
            theme: "grid",
            styles: {
                fontSize: 8,
                cellPadding: 2.5,
                valign: "middle",
                textColor: dark,
                lineColor: [203, 213, 225],
                lineWidth: 0.2,
            },
            headStyles: {
                fillColor: dark,
                textColor: white,
                fontStyle: "bold",
                fontSize: 8.5,
            },
            alternateRowStyles: { fillColor: light },
            columnStyles: {
                0:  { cellWidth: 10, halign: "center" },
                1:  { cellWidth: 28 },
                2:  { cellWidth: 44 },
                3:  { cellWidth: 20, halign: "center" },
                4:  { halign: "right" },
                5:  { halign: "right" },
                6:  { halign: "right" },
                7:  { halign: "right" },
                8:  { cellWidth: 20, halign: "center" },
                9:  { cellWidth: 24, halign: "center" },
            },
            didDrawCell: (hookData) => {
                // Colour the Status cell
                if (hookData.section === "body" && hookData.column.index === 8) {
                    const label = String(hookData.cell.raw || "");
                    const [r, g, b] = statusFill(label);
                    const { x, y, width, height } = hookData.cell;
                    doc.setFillColor(r, g, b);
                    doc.roundedRect(x + 1.5, y + 1.5, width - 3, height - 3, 1.5, 1.5, "F");
                    doc.setTextColor(...white);
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(7.5);
                    doc.text(label, x + width / 2, y + height / 2 + 0.5, { align: "center", baseline: "middle" });
                }
            },
        });

        // ── Summary footer bar ───────────────────────────────────────────────
        const finalY = (doc as any).lastAutoTable.finalY + 5;
        const summaryLines = Object.entries(totalsMap).map(
            ([cur, v]) => `${cur} — Discount: ${v.discount.toLocaleString()}   Total: ${v.total.toLocaleString()}   Paid: ${v.paid.toLocaleString()}   Remain: ${v.remain.toLocaleString()}`
        );
        const barH = 8 + summaryLines.length * 7;
        if (finalY + barH < pageHeight - 10) {
            doc.setFillColor(...dark);
            doc.roundedRect(10, finalY, pageWidth - 20, barH, 2, 2, "F");
            doc.setTextColor(...white);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text("SUMMARY", 17, finalY + 6.5);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);
            summaryLines.forEach((line, i) => {
                doc.text(line, pageWidth - 17, finalY + 6.5 + i * 7, { align: "right" });
            });
        }

        // ── Page numbers ─────────────────────────────────────────────────────
        const total = (doc as any).internal.getNumberOfPages();
        for (let i = 1; i <= total; i++) {
            doc.setPage(i);
            doc.setFontSize(7);
            doc.setTextColor(148, 163, 184);
            doc.setFont("helvetica", "normal");
            doc.text(`Page ${i} of ${total}`, pageWidth / 2, pageHeight - 4, { align: "center" });
        }

        doc.save(`Sales_Report_${today.replace(/\//g, "-")}.pdf`);
    };

    const columns = [
        {
            title: t("invoice_number"),
            dataIndex: "invoiceNumber",
            render: (text: string, record: SellInvoice) => (
                <Button
                    type="link"
                    onClick={() => openDrawer(record)}
                    icon={<EyeOutlined />}
                    style={{ padding: 0 }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t("customer"),
            dataIndex: "customer",
            render: (_: any, record: any) => (
                <Text strong>
                    {getCustomerName(record.customer, record.customerName, customers)}
                </Text>
            ),
        },
        {
            title: t("currency"),
            dataIndex: "currency",
            render: (cur: string) => (
                <Tag color={cur === "USD" ? "blue" : "gold"}>{cur}</Tag>
            ),
        },
        {
            title: t("total"),
            dataIndex: "total",
            render: (value: number, record: SellInvoice) => (
                <Text>
                    {value.toLocaleString(undefined, {
                        style: "currency",
                        currency: getValidCurrency(record.currency),
                        minimumFractionDigits: 0,
                    })}
                </Text>
            ),
        },
        {
            title: t("paid_status"),
            key: "paidStatus",
            render: (_: any, record: any) => {
                const status = getPaidStatus(record.paid, record.remain, t);
                return (
                    <Tag color={statusColors[status] || "default"}>
                        {status}
                    </Tag>
                );
            },
        },
        {
            title: t("date"),
            dataIndex: "date",
            render: (date: string) => {
                if (!date) return "-";
                let locale = "en-GB";
                if (i18n.language === "ar") locale = "ar-EG";
                else if (i18n.language === "ckb") locale = "ku";
                return new Date(date).toLocaleDateString(locale);
            },
        },
        {
            title: t("action"),
            key: "action",
            render: (_: any, record: SellInvoice) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => onEdit(record)}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        onClick={() => deleteSale(record.id as string)}
                    />
                    {/* Print Button */}
                    <Button
                        icon={<PrinterOutlined />}
                        size="small"
                        onClick={() => setPrintInvoice(record)}
                    >
                        {t("print")}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{
            background: "#fff",
            borderRadius: 16,
            padding: 24,
            boxShadow: "0 2px 8px #f0f1f2",
            direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr'
        }}>
            <PageNavigation title={t("sales_invoices") || "Sales Invoices"} />
            <Space direction="vertical" style={{ width: "100%" }}>
                <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
                    <div>
                        <Text type="secondary">{t("sales_invoices_subtitle")}</Text>
                    </div>
                    <Space>
                        <Button
                            icon={<FilePdfOutlined />}
                            onClick={handleDownloadPDF}
                            disabled={filteredSales.length === 0}
                            style={{
                                background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
                                borderColor: "#b91c1c",
                                color: "#fff",
                                fontWeight: 600,
                                boxShadow: "0 2px 8px rgba(239,68,68,0.35)",
                                borderRadius: 8,
                            }}
                        >
                            {t("download_pdf") || "Download PDF"}
                        </Button>
                        {onAdd && (
                            <Button
                                icon={<PlusOutlined />}
                                type="primary"
                                onClick={onAdd}
                                style={{ borderRadius: 8 }}
                            >
                                {t("new_sale")}
                            </Button>
                        )}
                    </Space>
                </Space>
                <Space wrap>
                    <Input.Search
                        allowClear
                        placeholder={t("search_invoice_customer_phone") || "Search by invoice, customer or phone"}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 300, maxWidth: "100%" }}
                    />
                    <Select
                        value={statusFilter}
                        onChange={(value) => setStatusFilter(value)}
                        style={{ width: 180 }}
                        options={[
                            { value: "all", label: t("all_statuses") || "All statuses" },
                            { value: "paid", label: t("paid") },
                            { value: "unpaid", label: t("unpaid") },
                            { value: "partial", label: t("partial") },
                        ]}
                    />
                </Space>
                <Spin spinning={sales.length === 0}>
                    <div style={{ minHeight: 200 }}>
                        <Table
                            columns={columns}
                            dataSource={filteredSales}
                            rowKey="id"
                            pagination={{ pageSize: 10 }}
                            locale={{
                            emptyText: <Empty description={t("no_sales_found")} />,
                        }}
                    />
                    </div>
                </Spin>
            </Space>
            {/* Drawer for Details */}
            <Drawer
                title={
                    selectedSale
                        ? `${t("invoice_number")} ${selectedSale.invoiceNumber}`
                        : t("sale_details")
                }
                placement="right"
                onClose={closeDrawer}
                open={drawerOpen}
                width={500}
            >
                {selectedSale ? (
                    <>
                        {/* <InvoicePrintHandler
                            invoice={selectedSale}
                            type="sale"
                            getCustomerOrSupplierName={() => getCustomerName(selectedSale.customer, selectedSale.customerName, customers)}
                            getProductName={(id) => getProductName(id, products)}
                        /> */}
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label={t("customer")}>
                                {selectedSale.customer && typeof selectedSale.customer === "object" && "name" in selectedSale.customer
                                    ? (selectedSale.customer as { name?: string }).name
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("phone")}>
                                {selectedSale.customer && typeof selectedSale.customer === "object" && "phone" in selectedSale.customer
                                    ? (selectedSale.customer as { phone?: string }).phone
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("date")}>
                                {selectedSale.date
                                    ? new Date(selectedSale.date).toLocaleString()
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("currency")}>
                                <Tag color={selectedSale.currency === "USD" ? "blue" : "gold"}>
                                    {selectedSale.currency}
                                </Tag>
                            </Descriptions.Item>
                            {!!selectedSale.discount && selectedSale.discount > 0 && (
                                <>
                                    <Descriptions.Item label={t("subtotal")}>
                                        <Text>
                                            {((selectedSale.total || 0) + (selectedSale.discount || 0)).toLocaleString(undefined, {
                                                style: "currency",
                                                currency: getValidCurrency(selectedSale.currency),
                                                minimumFractionDigits: 0,
                                            })}
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t("discount")}>
                                        <Text type="danger">
                                            - {selectedSale.discount.toLocaleString(undefined, {
                                                style: "currency",
                                                currency: getValidCurrency(selectedSale.currency),
                                                minimumFractionDigits: 0,
                                            })}
                                        </Text>
                                    </Descriptions.Item>
                                </>
                            )}
                            <Descriptions.Item label={t("total")}>
                                <Text strong>
                                    {selectedSale.total?.toLocaleString(undefined, {
                                        style: "currency",
                                        currency: getValidCurrency(selectedSale.currency),
                                        minimumFractionDigits: 0,
                                    })}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("paid")}>
                                <Text>
                                    {selectedSale.paid?.toLocaleString(undefined, {
                                        style: "currency",
                                        currency: getValidCurrency(selectedSale.currency),
                                        minimumFractionDigits: 0,
                                    })}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("remain")}>
                                <Text>
                                    {selectedSale.remain?.toLocaleString(undefined, {
                                        style: "currency",
                                        currency: getValidCurrency(selectedSale.currency),
                                        minimumFractionDigits: 0,
                                    })}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("paid_status")}>
                                <Tag color={statusColors[getPaidStatus(selectedSale.paid, selectedSale.remain, t)]}>
                                    {getPaidStatus(selectedSale.paid, selectedSale.remain, t)}
                                </Tag>
                            </Descriptions.Item>
                        </Descriptions>
                        <Divider orientation="left" style={{ marginTop: 16 }}>
                            {t("sale_items")}
                        </Divider>
                        <List
                            itemLayout="vertical"
                            dataSource={selectedSale.items}
                            bordered
                            renderItem={(item: any, idx) => (
                                <List.Item key={idx}>
                                    <List.Item.Meta
                                        title={
                                            <span>
                                                <b>{getProductName(item.product, products)}</b> <Tag>{item.unit}</Tag>
                                            </span>
                                        }
                                        description={
                                            <>
                                                <span>{t("qty")}: {item.qty} | {t("price")}: {item.sellPrice?.toLocaleString()} {selectedSale.currency}</span>
                                                {(item.unit === "roll" || item.unit === "meter") && (
                                                    <> | {t("length")}: {item.unitLength}</>
                                                )}
                                                {item.discount && <> | {t("discount")}: {item.discount}%</>}
                                            </>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </>
                ) : (
                    <Empty description={t("no_sale_selected")} />
                )}
            </Drawer>

            {/* Print Handler (hidden, triggers print) */}
            {printInvoice && (
                <InvoicePrintHandler
                    invoice={printInvoice}
                    type="sale"
                    getCustomerOrSupplierName={() => getCustomerName(printInvoice.customer, printInvoice.customerName, customers)}
                    getProductName={(id) => getProductName(id, products)}
                    onAfterPrint={() => setPrintInvoice(null)}
                />
            )}
        </div>
    );
};

export default SaleList;
